/**
 * AppShell — three-region layout (sidebar + topbar + scrolling content),
 * mirroring the design. In-app navigation is an activeScreen state plus an
 * optional detail overlay (document editor/view). Sidebar collapses to a
 * drawer on mobile.
 */
import React, { useState } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../utils/responsive';
import { showToast } from '../components/skToast';
import Sidebar from '../shell/sidebar';
import Topbar from '../shell/topbar';
import SkIcon from '../components/skIcon';
import { SCREEN_TITLES } from './navModel';
import type { AppScreen } from './types';
import { DocType } from '../schema/billing/common';
import { DocRow } from '../utils/format';

import Dashboard from '../screens/dashboard';
import DocumentList from '../screens/documentList';
import DocumentView, { OpenEditor } from '../screens/documentView';
import DocumentEditor, { EditorPrefill } from '../screens/documentEditor';
import Payments from '../screens/payments';
import Banking from '../screens/banking';
import Statements from '../screens/statements';
import Contacts from '../screens/contacts';
import ChartOfAccounts from '../screens/coa';
import Journals from '../screens/journals';
import Periods from '../screens/periods';
import Reports from '../screens/reports';
import Migration from '../screens/migration';
import Settings from '../screens/settings';
import Placeholder from '../screens/placeholder';
import ComingSoon from '../components/comingSoon';
import NewSheet from '../components/modals/newSheet';

const LIST_TYPES: Partial<Record<AppScreen, { type: DocType; newLabel: string }>> = {
  invoices: { type: 'INVOICE', newLabel: 'New Invoice' },
  quotes: { type: 'QUOTE', newLabel: 'New Quote' },
  'credit-notes': { type: 'CREDIT_NOTE', newLabel: 'New Credit Note' },
  bills: { type: 'BILL', newLabel: 'New Bill' },
  'vendor-credits': { type: 'VENDOR_CREDIT', newLabel: 'New Vendor Credit' },
  expenses: { type: 'EXPENSE', newLabel: 'New Expense' },
};

type Detail = { kind: 'view' | 'editor'; doc: DocRow | null; docType?: DocType; prefill?: EditorPrefill } | null;

const AppShell: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const { isMobile } = useResponsive();
  const [active, setActive] = useState<AppScreen>('dashboard');
  const [detail, setDetail] = useState<Detail>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);

  const navigate = (screen: AppScreen) => {
    setActive(screen);
    setDetail(null);
    setDrawerOpen(false);
  };

  const EDITABLE_DRAFT_TYPES: DocType[] = ['INVOICE', 'QUOTE', 'CREDIT_NOTE', 'BILL', 'VENDOR_CREDIT', 'EXPENSE'];
  const openDoc = (doc: DocRow) => {
    if (doc.status === 'DRAFT' && EDITABLE_DRAFT_TYPES.includes(doc.type)) setDetail({ kind: 'editor', doc });
    else setDetail({ kind: 'view', doc });
  };
  const openNew = (docType: DocType = 'INVOICE') => setDetail({ kind: 'editor', doc: null, docType });
  // From a document: a converted invoice draft, or a credit note pre-filled from its invoice.
  const openEditor: OpenEditor = ({ doc: target, docType, prefill }) => setDetail({ kind: 'editor', doc: target, docType, prefill });

  const title = detail
    ? detail.kind === 'editor'
      ? 'Editor'
      : detail.doc?.number || 'Document'
    : SCREEN_TITLES[active] || 'SK Billing';

  const renderScreen = () => {
    if (detail) {
      return detail.kind === 'editor' ? (
        <DocumentEditor key={detail.doc?.id ?? `new-${detail.docType}`} doc={detail.doc} docType={detail.docType} prefill={detail.prefill} onBack={() => setDetail(null)} />
      ) : (
        <DocumentView doc={detail.doc as DocRow} onBack={() => setDetail(null)} onOpenEditor={openEditor} />
      );
    }
    const list = LIST_TYPES[active];
    if (list) return <DocumentList docType={list.type} newLabel={list.newLabel} onOpenDoc={openDoc} onNew={() => openNew(list.type)} />;
    switch (active) {
      case 'dashboard':
        return <Dashboard onNavigate={navigate} />;
      case 'payments':
        return <Payments />;
      case 'banking':
        return <Banking />;
      case 'contacts':
        return <Contacts />;
      case 'statements':
        return <Statements />;
      case 'coa':
        // Chart of Accounts implementation is preserved in screens/coa.tsx; we
        // present a premium "coming soon" card until it's ready to ship.
        return (
          <ComingSoon
            icon="book"
            title="Your Chart of Accounts is on the way"
            blurb="A beautifully organized ledger of every account — assets, liabilities, income and expenses — is coming soon to give you total clarity and confidence over your books."
            features={["Grouped, color-coded account tree", "Drill into any balance in a single tap", "GST-ready ledger codes, set up for you"]}
          />
        );
      case 'journals':
        return <Journals />;
      case 'periods':
        return (
          <ComingSoon
            icon="calendar"
            title="Accounting Periods, arriving soon"
            blurb="Soon you’ll open and close financial periods with total confidence — lock the past, keep the present tidy, and never miss a filing again."
            features={["Lock periods to protect filed data", "Effortless month & year-end closes", "A clean, audit-friendly history"]}
          />
        );
      case 'reports':
        return (
          <ComingSoon
            icon="chart"
            title="Powerful Reports are almost here"
            blurb="Rich, real-time financial reports — P&L, balance sheet and GST summaries — are on their way to turn your numbers into decisions you can trust."
            features={["Live P&L and balance sheet", "GST & tax summaries at a glance", "One-click export to PDF and Excel"]}
          />
        );
      case 'migration':
        return (
          <ComingSoon
            icon="refresh"
            title="Effortless Migration, coming soon"
            blurb="Bringing your data across from your old tool is about to get delightfully simple — import customers, invoices and balances in just a few minutes."
            features={["One-click import from your old tool", "A guided wizard maps every field", "Zero-downtime, stress-free switchover"]}
          />
        );
      case 'settings':
        return <Settings />;
      default:
        return <Placeholder title={title} />;
    }
  };

  return (
    <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.paper, height: Platform.OS === 'web' ? ('100vh' as any) : '100%', overflow: 'hidden' }}>
      {!isMobile && <Sidebar active={active} onNavigate={navigate} />}
      {isMobile && drawerOpen && (
        <View style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, zIndex: 50, flexDirection: 'row' }}>
          <Sidebar active={active} onNavigate={navigate} />
          <Pressable style={{ flex: 1, backgroundColor: 'rgba(16,24,40,0.42)' }} onPress={() => setDrawerOpen(false)} />
        </View>
      )}

      <View style={{ flex: 1, minWidth: 0, height: '100%' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', zIndex: 30, ...(Platform.OS === 'web' ? { position: 'relative' as any } : null) }}>
          {isMobile && (
            <Pressable onPress={() => setDrawerOpen(true)} style={{ width: 44, height: 56, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderBottomWidth: 1, borderBottomColor: colors.line }}>
              <SkIcon name="fileText" size={18} color={colors.ink2} />
            </Pressable>
          )}
          <View style={{ flex: 1 }}>
            <Topbar title={title} onNewPress={() => setNewOpen(true)} />
          </View>
        </View>

        <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={{ flexGrow: 1, paddingVertical: 24 }}>
          {renderScreen()}
        </ScrollView>
      </View>

      <NewSheet visible={newOpen} onClose={() => setNewOpen(false)} onCreateInvoice={() => openNew()} />
    </View>
  );
};

export default AppShell;
